import { useState, useMemo } from "react"
import { agrogestao } from "@/api/agrogestaoClient"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { motion, AnimatePresence } from "framer-motion"
import Paginacao from "../components/shared/Paginacao"
import { Droplet, Plus, Edit2, Trash2, Loader2, DollarSign, Calendar, Users, Calculator, Check, ChevronsUpDown, AlertCircle, Sun, Moon, Sunset, Archive, ArchiveRestore, Ban, CheckCircle2, LayoutGrid, List } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { createPageUrl } from "../utils"
import { Checkbox } from "@/components/ui/checkbox"

const TURNOS = [
  { value: 'manha', label: 'Manhã' },
  { value: 'tarde', label: 'Tarde' },
  { value: 'noite', label: 'Noite' }
]

const CATEGORIAS = [
  { value: 'bovino_leite', label: 'Bovino de Leite' },
  { value: 'bovino_corte', label: 'Bovino de Corte' },
  { value: 'suino', label: 'Suíno' },
  { value: 'aves', label: 'Aves' },
  { value: 'ovino', label: 'Ovinos' },
  { value: 'caprino', label: 'Caprinos' },
  { value: 'equino', label: 'Equinos' },
  { value: 'outro', label: 'Outros' }
]

const MILK_PRODUCING_TYPES = ['bovino_leite', 'caprino', 'ovino', 'outro']

export default function ProducaoLeite() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [filter, setFilter] = useState({ categoria: '', inicio: '', fim: '' })
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    turno: 'manha',
    volume_litros: '',
    preco_litro: '',
    categoria: 'bovino_leite',
    lote: '',
    observacoes: '',
    gordura_pct: '',
    proteina_pct: '',
    lactose_pct: '',
    ccs: '',
    cbt: '',
    acidez_dornic: '',
    temperatura_c: ''
  })
  const [errorMsg, setErrorMsg] = useState("")
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  
  // Movimentações Tanque States
  const [movPage, setMovPage] = useState(1)
  const [movPageSize, setMovPageSize] = useState(10)
  const [movFilter, setMovFilter] = useState({ inicio: '', fim: '', tipo: 'todos' })
  const [viewMode, setViewMode] = useState('card')

  const { data: producoes = [], isLoading } = useQuery({
    queryKey: ["producao_leite", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.ProducaoLeite.filter({ propriedade_id: propriedadeId }, "-data")
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const { data: tanques = [] } = useQuery({
    queryKey: ["tanques_leite", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.TanqueLeite.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const { data: animais = [] } = useQuery({
    queryKey: ["animais_leite", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Animal.filter({ propriedade_id: propriedadeId, categoria: 'bovino_leite' })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const lotesDisponiveis = useMemo(() => {
    return [...new Set(animais.map(a => a.lote).filter(l => l && l.trim() !== ''))].sort()
  }, [animais])

  const movimentosQuery = useQuery({
    queryKey: ["movimentacoes_tanque_leite", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.MovimentacaoTanqueLeite.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const movimentos = useMemo(() => movimentosQuery.data || [], [movimentosQuery.data])

  const filteredMovimentos = useMemo(() => {
    return movimentos.filter(m => {
      if (movFilter.tipo !== 'todos' && m.tipo !== movFilter.tipo) return false
      if (movFilter.inicio && m.data < movFilter.inicio) return false
      if (movFilter.fim && m.data > movFilter.fim) return false
      return true
    }).sort((a, b) => new Date(b.data) - new Date(a.data))
  }, [movimentos, movFilter])

  const movStart = (movPage - 1) * movPageSize
  const movPageItems = useMemo(() => filteredMovimentos.slice(movStart, movStart + movPageSize), [filteredMovimentos, movStart, movPageSize])

  const { data: parceiros = [] } = useQuery({
    queryKey: ["parceiros", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const tanquesStats = useMemo(() => {
    return tanques.map(t => {
      const movs = movimentos.filter(m => m.tanque_id === t.id)
      const entradas = movs.filter(m => m.tipo === 'entrada')
      const saidas = movs.filter(m => m.tipo === 'saida')
      const volume = entradas.reduce((a, m) => a + (m.volume_litros || 0), 0) - saidas.reduce((a, m) => a + (m.volume_litros || 0), 0)
      const mediaGordura = entradas.length ? (entradas.reduce((a, m) => a + ((m.qualidade_gordura_pct || 0) * (m.volume_litros || 0)), 0) / entradas.reduce((a, m) => a + (m.volume_litros || 0), 0)) : null
      const mediaProteina = entradas.length ? (entradas.reduce((a, m) => a + ((m.qualidade_proteina_pct || 0) * (m.volume_litros || 0)), 0) / entradas.reduce((a, m) => a + (m.volume_litros || 0), 0)) : null
      return {
        ...t,
        volume_atual: volume,
        disponivel: (t.capacidade_litros || 0) - volume,
        mediaGordura,
        mediaProteina
      }
    })
  }, [tanques, movimentos])

  const [tanqueDialogOpen, setTanqueDialogOpen] = useState(false)
  const [novoTanque, setNovoTanque] = useState({ nome: '', capacidade_litros: '' })
  const [envioTanque, setEnvioTanque] = useState({ open: false, producao: null, tanque_id: '', volume: 0, data: new Date().toISOString().split('T')[0], qualidade: {} })
  const [vendaTanque, setVendaTanque] = useState({ open: false, tanque: null, volume_max: 0, data: new Date().toISOString().split('T')[0], volume_vendido: '', preco_litro: '', cliente: '', usar_qualidade: false, gordura_pct: '', proteina_pct: '', ref_gordura_pct: '3.5', ref_proteina_pct: '3.2', coef_gordura: '0.05', coef_proteina: '0.06' })
  const [isSavingVenda, setIsSavingVenda] = useState(false)
  const [openCombobox, setOpenCombobox] = useState(false)

  const coefKey = 'qualidade_coef_' + propriedadeId
  function getCoef(cliente, tanqueId) {
    try {
      const raw = localStorage.getItem(coefKey)
      const map = raw ? JSON.parse(raw) : {}
      return map[`${cliente || ''}::${tanqueId}`]
    } catch (e) {
      return null
    }
  }
  function setCoef(cliente, tanqueId, cfg) {
    try {
      const raw = localStorage.getItem(coefKey)
      const map = raw ? JSON.parse(raw) : {}
      map[`${cliente || ''}::${tanqueId}`] = cfg
      localStorage.setItem(coefKey, JSON.stringify(map))
    } catch (e) {}
  }

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me()
      const payload = {
        data: data.data,
        turno: data.turno,
        volume_litros: data.volume_litros ? parseFloat(data.volume_litros) : null,
        preco_litro: data.preco_litro ? parseFloat(data.preco_litro) : null,
        categoria: data.categoria || null,
        lote: data.lote || null,
        observacoes: data.observacoes || null,
        gordura_pct: data.gordura_pct ? parseFloat(data.gordura_pct) : null,
        proteina_pct: data.proteina_pct ? parseFloat(data.proteina_pct) : null,
        lactose_pct: data.lactose_pct ? parseFloat(data.lactose_pct) : null,
        ccs: data.ccs ? parseInt(data.ccs) : null,
        cbt: data.cbt ? parseInt(data.cbt) : null,
        acidez_dornic: data.acidez_dornic ? parseFloat(data.acidez_dornic) : null,
        temperatura_c: data.temperatura_c ? parseFloat(data.temperatura_c) : null,
        propriedade_id: propriedadeId,
        created_by: u?.email || null
      }
      return agrogestao.entities.ProducaoLeite.create(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producao_leite", propriedadeId] })
      resetForm()
    },
    onError: (err) => setErrorMsg(err?.message || "Erro ao registrar produção")
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.ProducaoLeite.update(id, {
      data: data.data,
      turno: data.turno,
      volume_litros: data.volume_litros ? parseFloat(data.volume_litros) : null,
      preco_litro: data.preco_litro ? parseFloat(data.preco_litro) : null,
      categoria: data.categoria || null,
      lote: data.lote || null,
      observacoes: data.observacoes || null,
      gordura_pct: data.gordura_pct ? parseFloat(data.gordura_pct) : null,
      proteina_pct: data.proteina_pct ? parseFloat(data.proteina_pct) : null,
      lactose_pct: data.lactose_pct ? parseFloat(data.lactose_pct) : null,
      ccs: data.ccs ? parseInt(data.ccs) : null,
      cbt: data.cbt ? parseInt(data.cbt) : null,
      acidez_dornic: data.acidez_dornic ? parseFloat(data.acidez_dornic) : null,
      temperatura_c: data.temperatura_c ? parseFloat(data.temperatura_c) : null
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producao_leite", propriedadeId] })
      resetForm()
    },
    onError: (err) => setErrorMsg(err?.message || "Erro ao atualizar produção")
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.ProducaoLeite.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["producao_leite", propriedadeId] })
  })

  const updateTanqueMutation = useMutation({
    mutationFn: ({ id, status }) => agrogestao.entities.TanqueLeite.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tanques_leite", propriedadeId] }),
    onError: (err) => setErrorMsg(err?.message || "Erro ao atualizar tanque")
  })

  const filtered = useMemo(() => {
    return producoes.filter(p => {
      if (filter.categoria && p.categoria !== filter.categoria) return false
      if (filter.inicio && p.data < filter.inicio) return false
      if (filter.fim && p.data > filter.fim) return false
      if (searchTerm) {
        const s = searchTerm.toLowerCase()
        const l = (p.lote || '').toLowerCase()
        const o = (p.observacoes || '').toLowerCase()
        if (!l.includes(s) && !o.includes(s)) return false
      }
      return true
    })
  }, [producoes, filter, searchTerm])

  const start = (currentPage - 1) * pageSize
  const pageItems = useMemo(() => filtered.slice(start, start + pageSize), [filtered, start, pageSize])

  const totalLitros = useMemo(() => filtered.reduce((acc, p) => acc + (typeof p.volume_litros === 'number' ? p.volume_litros : 0), 0), [filtered])
  const receitaEstimada = useMemo(() => filtered.reduce((acc, p) => {
    const v = typeof p.volume_litros === 'number' ? p.volume_litros : null
    const pr = typeof p.preco_litro === 'number' ? p.preco_litro : null
    if (v !== null && pr !== null) return acc + v * pr
    return acc
  }, 0), [filtered])
  const mediaPorDia = useMemo(() => {
    const map = new Map()
    filtered.forEach(p => {
      const d = p.data
      const v = typeof p.volume_litros === 'number' ? p.volume_litros : 0
      map.set(d, (map.get(d) || 0) + v)
    })
    const vals = Array.from(map.values())
    if (vals.length === 0) return 0
    return vals.reduce((a, b) => a + b, 0) / vals.length
  }, [filtered])
  const litrosPorTurno = useMemo(() => {
    return ['manha','tarde','noite'].map(t => (
      { turno: t, litros: filtered.filter(p => p.turno === t).reduce((acc, p) => acc + (typeof p.volume_litros === 'number' ? p.volume_litros : 0), 0) }
    ))
  }, [filtered])

  const qualidadeMedias = useMemo(() => {
    const nums = (arr) => arr.filter(v => typeof v === 'number')
    const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null
    return {
      gordura: avg(nums(filtered.map(p => p.gordura_pct))),
      proteina: avg(nums(filtered.map(p => p.proteina_pct))),
      lactose: avg(nums(filtered.map(p => p.lactose_pct))),
      ccs: avg(nums(filtered.map(p => p.ccs))),
      cbt: avg(nums(filtered.map(p => p.cbt)))
    }
  }, [filtered])

  const tendenciaMensal = useMemo(() => {
    const map = new Map()
    filtered.forEach(p => {
      const key = (p.data || '').slice(0, 7)
      if (!key) return
      const cur = map.get(key) || { ccs: [], cbt: [] }
      if (typeof p.ccs === 'number') cur.ccs.push(p.ccs)
      if (typeof p.cbt === 'number') cur.cbt.push(p.cbt)
      map.set(key, cur)
    })
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
    return entries.map(([mes, vals]) => ({
      mes,
      ccs: vals.ccs.length ? Math.round(vals.ccs.reduce((a, b) => a + b, 0) / vals.ccs.length) : null,
      cbt: vals.cbt.length ? Math.round(vals.cbt.reduce((a, b) => a + b, 0) / vals.cbt.length) : null
    }))
  }, [filtered])

  function turnoBoxClasses(turno) {
    if (turno === 'manha') return 'bg-amber-100 text-amber-700'
    if (turno === 'tarde') return 'bg-orange-100 text-orange-700'
    if (turno === 'noite') return 'bg-indigo-100 text-indigo-700'
    return 'bg-stone-100 text-stone-700'
  }

  const resetForm = () => {
    setFormData({ 
      data: new Date().toISOString().split('T')[0], turno: 'manha', volume_litros: '', preco_litro: '', 
      categoria: 'bovino_leite', lote: '', observacoes: '', gordura_pct: '', proteina_pct: '', lactose_pct: '',
      ccs: '', cbt: '', acidez_dornic: '', temperatura_c: ''
    })
    setEditingId(null)
    setDialogOpen(false)
    setErrorMsg("")
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const vol = parseFloat(formData.volume_litros || 0)
    if (!vol || vol <= 0) { setErrorMsg("Volume inválido"); return }
    if (editingId) updateMutation.mutate({ id: editingId, data: formData })
    else createMutation.mutate(formData)
  }

  const handleConfirmEnvioTanque = async () => {
    try {
      setErrorMsg("")
      if (!envioTanque.tanque_id) {
        setErrorMsg('Selecione um tanque de destino.')
        return
      }

      const tanque = tanquesStats.find(t => t.id === envioTanque.tanque_id)
      const volume = parseFloat(envioTanque.volume || 0)

      if (volume <= 0) {
        setErrorMsg('O volume deve ser maior que zero.')
        return
      }

      if (tanque && tanque.capacidade_litros && (tanque.volume_atual + volume) > tanque.capacidade_litros) {
        setErrorMsg(`O volume excede a capacidade disponível do tanque (Disponível: ${tanque.disponivel.toFixed(2)} L).`)
        return
      }

      const u = await agrogestao.auth.me()
      
      await agrogestao.entities.MovimentacaoTanqueLeite.create({
        propriedade_id: propriedadeId,
        tanque_id: envioTanque.tanque_id,
        tipo: 'entrada',
        data: envioTanque.data,
        volume_litros: volume,
        origem_producao_id: envioTanque.producao?.id || null,
        qualidade_gordura_pct: envioTanque.qualidade?.gordura_pct ? parseFloat(envioTanque.qualidade.gordura_pct) : (envioTanque.producao?.gordura_pct || null),
        qualidade_proteina_pct: envioTanque.qualidade?.proteina_pct ? parseFloat(envioTanque.qualidade.proteina_pct) : (envioTanque.producao?.proteina_pct || null),
        qualidade_lactose_pct: envioTanque.producao?.lactose_pct || null,
        qualidade_ccs: envioTanque.producao?.ccs || null,
        qualidade_cbt: envioTanque.producao?.cbt || null,
        qualidade_acidez_dornic: envioTanque.qualidade?.acidez_dornic ? parseFloat(envioTanque.qualidade.acidez_dornic) : (envioTanque.producao?.acidez_dornic || null),
        qualidade_temperatura_c: envioTanque.producao?.temperatura_c || null,
        created_by: u?.email || null
      })

      queryClient.invalidateQueries({ queryKey: ['movimentacoes_tanque_leite', propriedadeId] })
      queryClient.invalidateQueries({ queryKey: ['tanques_leite', propriedadeId] })
      setEnvioTanque({ open: false, producao: null, tanque_id: '', volume: 0, data: new Date().toISOString().split('T')[0], qualidade: {} })
      setErrorMsg('')
    } catch (err) {
      console.error(err)
      setErrorMsg(err.message || 'Erro ao enviar para o tanque.')
    }
  }

  if (!propriedadeId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Selecione uma propriedade</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-700 mb-4">Para registrar produção de leite, selecione primeiro a propriedade em Propriedades.</p>
            <a href="/Propriedades" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg">Ir para Propriedades</a>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Droplet className="w-8 h-8 text-[#40916C] animate-bounce" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800">Produção de Leite</h1>
          <p className="text-stone-600 mt-1">Registre volumes por turno e acompanhe receitas</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
                <Plus className="w-5 h-5" />
                Novo Registro
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700">
                    <Droplet className="w-5 h-5" />
                  </div>
                  {editingId ? "Editar Produção" : "Nova Produção"}
                </DialogTitle>
                <DialogDescription>Informe os dados da ordenha realizada</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                
                {/* Data e Turno */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.1 }}
                  className="space-y-3"
                >
                  <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Quando ocorreu?</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                      <Input 
                        type="date" 
                        value={formData.data} 
                        onChange={(e) => setFormData({ ...formData, data: e.target.value })} 
                        required 
                        className="pl-9"
                      />
                    </div>
                    <div className="flex bg-stone-100 p-1 rounded-lg">
                      {[
                        { id: 'manha', label: 'Manhã', icon: Sun, activeClass: 'bg-amber-100 text-amber-700 shadow-sm border-amber-200' },
                        { id: 'tarde', label: 'Tarde', icon: Sunset, activeClass: 'bg-orange-100 text-orange-700 shadow-sm border-orange-200' },
                        { id: 'noite', label: 'Noite', icon: Moon, activeClass: 'bg-indigo-100 text-indigo-700 shadow-sm border-indigo-200' }
                      ].map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, turno: t.id })}
                          className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${
                            formData.turno === t.id 
                              ? `${t.activeClass} border` 
                              : 'text-stone-500 hover:text-stone-700 hover:bg-stone-200'
                          }`}
                        >
                          <t.icon className="w-4 h-4" />
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Volume e Preço */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.2 }}
                  className="space-y-3"
                >
                  <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Produção</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Volume (Litros) *</Label>
                      <div className="relative">
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={formData.volume_litros} 
                          onChange={(e) => setFormData({ ...formData, volume_litros: e.target.value })} 
                          required 
                          className="text-lg font-semibold h-11 border-stone-300 focus:border-emerald-500"
                          placeholder="0.00"
                        />
                        <span className="absolute right-3 top-3 text-sm text-stone-400 font-medium">L</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-stone-600">Preço Estimado (R$/L)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-stone-400 font-medium">R$</span>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={formData.preco_litro} 
                          onChange={(e) => setFormData({ ...formData, preco_litro: e.target.value })} 
                          className="pl-9 h-11"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Categoria e Lote */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.3 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="space-y-1">
                    <Label className="text-xs text-stone-600">Categoria Animal</Label>
                    <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS.filter(c => MILK_PRODUCING_TYPES.includes(c.value)).map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 flex flex-col">
                    <Label className="text-xs text-stone-600">Lote</Label>
                    <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCombobox}
                          className="w-full justify-between h-10 font-normal px-3"
                        >
                          {formData.lote
                            ? (lotesDisponiveis.find((lote) => lote === formData.lote) || formData.lote)
                            : "Selecione um lote..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                        <Command>
                          <CommandInput placeholder="Procurar lote..." />
                          <CommandList>
                            <CommandEmpty>Nenhum lote encontrado.</CommandEmpty>
                            <CommandGroup>
                              {lotesDisponiveis.map((lote) => (
                                <CommandItem
                                  key={lote}
                                  value={lote}
                                  onSelect={(currentValue) => {
                                    setFormData({ ...formData, lote: currentValue === formData.lote ? "" : currentValue })
                                    setOpenCombobox(false)
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.lote === lote ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {lote}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </motion.div>

                {/* Qualidade (Collapsible-like visual) */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.4 }}
                  className="bg-stone-50 rounded-lg p-3 border border-stone-100"
                >
                  <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3 block flex items-center gap-2">
                    <Calculator className="w-3 h-3" /> Métricas de Qualidade (Opcional)
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">Gordura %</Label>
                      <Input type="number" step="0.01" className="h-8 bg-white text-xs" value={formData.gordura_pct} onChange={(e) => setFormData({ ...formData, gordura_pct: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">Proteína %</Label>
                      <Input type="number" step="0.01" className="h-8 bg-white text-xs" value={formData.proteina_pct} onChange={(e) => setFormData({ ...formData, proteina_pct: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">Lactose %</Label>
                      <Input type="number" step="0.01" className="h-8 bg-white text-xs" value={formData.lactose_pct} onChange={(e) => setFormData({ ...formData, lactose_pct: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">CCS</Label>
                      <Input type="number" className="h-8 bg-white text-xs" value={formData.ccs} onChange={(e) => setFormData({ ...formData, ccs: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">CBT</Label>
                      <Input type="number" className="h-8 bg-white text-xs" value={formData.cbt} onChange={(e) => setFormData({ ...formData, cbt: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">Acidez (°D)</Label>
                      <Input type="number" step="0.1" className="h-8 bg-white text-xs" value={formData.acidez_dornic} onChange={(e) => setFormData({ ...formData, acidez_dornic: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-stone-500 uppercase">Temp. °C</Label>
                      <Input type="number" step="0.1" className="h-8 bg-white text-xs" value={formData.temperatura_c} onChange={(e) => setFormData({ ...formData, temperatura_c: e.target.value })} />
                    </div>
                  </div>
                </motion.div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#40916C] hover:bg-[#2D6A4F] px-6" disabled={createMutation.isPending || updateMutation.isPending}>
                    {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingId ? 'Salvar Alterações' : 'Registrar Produção'}
                  </Button>
                </div>
                {errorMsg ? (<div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded"><AlertCircle className="w-4 h-4" /> {errorMsg}</div>) : null}
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#40916C] to-[#2D6A4F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Litros</p>
            <p className="text-2xl font-bold">{totalLitros.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#D4A373] to-[#BC8A5F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Receita Estimada</p>
            <p className="text-2xl font-bold">{receitaEstimada.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Média por dia (L)</p>
            <p className="text-2xl font-bold">{mediaPorDia.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-lg">
          <CardContent className="p-4">
            <div className="text-xs/5 opacity-80">Por Turno</div>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {litrosPorTurno.map(it => (
                <div key={it.turno} className={`p-2 rounded text-center ${turnoBoxClasses(it.turno)}`}>
                  <div className="text-[10px] opacity-80">{TURNOS.find(t => t.value === it.turno)?.label || it.turno}</div>
                  <div className="font-semibold text-sm">{it.litros.toFixed(1)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="border-b pb-4">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
             <div className="flex items-center gap-4">
               <CardTitle>Histórico de Produção</CardTitle>
               <div className="flex bg-stone-100 p-1 rounded-lg border border-stone-200">
                 <button 
                   onClick={() => setViewMode('card')}
                   className={`p-1.5 rounded-md transition-all ${viewMode === 'card' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}
                   title="Visualização em Grade"
                 >
                   <LayoutGrid className="w-4 h-4" />
                 </button>
                 <button 
                   onClick={() => setViewMode('list')}
                   className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}
                   title="Visualização em Lista"
                 >
                   <List className="w-4 h-4" />
                 </button>
               </div>
             </div>
             <div className="flex flex-wrap gap-2 items-center">
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Categoria</Label>
                 <Select value={filter.categoria} onValueChange={(v) => setFilter({ ...filter, categoria: v === 'todas' ? '' : v })}>
                   <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Todas" /></SelectTrigger>
                   <SelectContent>
                     <SelectItem value="todas">Todas</SelectItem>
                     {CATEGORIAS.filter(c => ['bovino_leite', 'caprino', 'ovino', 'outro'].includes(c.value)).map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                   </SelectContent>
                 </Select>
               </div>
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Início</Label>
                 <Input type="date" className="h-8 text-xs w-32" value={filter.inicio} onChange={(e) => setFilter({ ...filter, inicio: e.target.value })} />
               </div>
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Fim</Label>
                 <Input type="date" className="h-8 text-xs w-32" value={filter.fim} onChange={(e) => setFilter({ ...filter, fim: e.target.value })} />
               </div>
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Buscar</Label>
                 <Input placeholder="Lote ou observação" className="h-8 text-xs w-40" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }} />
               </div>
             </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {filtered.length === 0 ? (
            <div className="text-stone-600">Nenhum registro de produção.</div>
          ) : (
            <>
              {viewMode === 'card' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {pageItems.map((p) => {
                      const valor = (typeof p.volume_litros === 'number' && typeof p.preco_litro === 'number') ? (p.volume_litros * p.preco_litro) : null
                      const enviadoAoTanque = movimentos.some(m => m.origem_producao_id === p.id)
                      
                      return (
                        <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="border rounded-xl p-4 bg-white border-l-4 border-[#40916C]">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-stone-800">{new Date(p.data).toLocaleDateString()}</div>
                              {enviadoAoTanque && (
                                <div className="flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100" title="Enviado ao tanque">
                                  <Droplet className="w-3 h-3" /> Enviado
                                </div>
                              )}
                            </div>
                            <Badge variant="secondary" className={`${turnoBoxClasses(p.turno)} border-transparent`}>{TURNOS.find(t => t.value === p.turno)?.label || p.turno}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">Volume (L)</p>
                              <p className="font-semibold text-stone-800">{typeof p.volume_litros === 'number' ? p.volume_litros.toFixed(2) : (p.volume_litros || '-')}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">Preço/L (R$)</p>
                              <p className="font-semibold text-stone-800">{typeof p.preco_litro === 'number' ? p.preco_litro.toFixed(2) : (p.preco_litro || '-')}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded col-span-2">
                              <p className="text-stone-500 text-xs">Valor (R$)</p>
                              <p className="font-semibold text-stone-800">{valor !== null ? valor.toFixed(2) : '-'}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">Gordura (%)</p>
                              <p className="font-semibold text-stone-800">{p.gordura_pct ?? '-'}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">Proteína (%)</p>
                              <p className="font-semibold text-stone-800">{p.proteina_pct ?? '-'}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">CCS</p>
                              <p className="font-semibold text-stone-800">{p.ccs ?? '-'}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded">
                              <p className="text-stone-500 text-xs">CBT</p>
                              <p className="font-semibold text-stone-800">{p.cbt ?? '-'}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded col-span-2">
                              <p className="text-stone-500 text-xs">Categoria</p>
                              <p className="font-semibold text-stone-800">{CATEGORIAS.find(c => c.value === p.categoria)?.label || (p.categoria || '-')}</p>
                            </div>
                            <div className="p-2 bg-stone-50 rounded col-span-2">
                              <p className="text-stone-500 text-xs">Lote</p>
                              <p className="font-semibold text-stone-800">{p.lote || '-'}</p>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => { setEditingId(p.id); setFormData({ data: p.data || new Date().toISOString().split('T')[0], turno: p.turno || 'manha', volume_litros: p.volume_litros?.toString() || '', preco_litro: p.preco_litro?.toString() || '', categoria: p.categoria || 'bovino_leite', lote: p.lote || '', observacoes: p.observacoes || '', gordura_pct: p.gordura_pct || '', proteina_pct: p.proteina_pct || '', lactose_pct: p.lactose_pct || '', ccs: p.ccs || '', cbt: p.cbt || '', acidez_dornic: p.acidez_dornic || '', temperatura_c: p.temperatura_c || '' }); setDialogOpen(true) }} 
                              className="text-stone-400 hover:text-stone-600"
                              disabled={enviadoAoTanque}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setEnvioTanque({ open: true, producao: p, volume: p.volume_litros || 0, data: p.data || new Date().toISOString().split('T')[0], tanque_id: tanques[0]?.id || '', qualidade: { gordura_pct: p.gordura_pct, proteina_pct: p.proteina_pct, lactose_pct: p.lactose_pct, ccs: p.ccs, cbt: p.cbt, acidez_dornic: p.acidez_dornic, temperatura_c: p.temperatura_c } })} 
                              className="text-[#1B4332] border-[#1B4332]"
                              disabled={enviadoAoTanque}
                            >
                              {enviadoAoTanque ? 'Enviado' : 'Enviar para tanque'}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => deleteMutation.mutate(p.id)} 
                              className="text-stone-400 hover:text-red-600"
                              disabled={enviadoAoTanque}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50">
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-stone-500">Data / Turno</th>
                        <th className="text-left py-3 px-4 font-medium text-stone-500">Volume</th>
                        <th className="text-left py-3 px-4 font-medium text-stone-500">Preço / Valor</th>
                        <th className="text-left py-3 px-4 font-medium text-stone-500">Categoria / Lote</th>
                        <th className="text-left py-3 px-4 font-medium text-stone-500">Qualidade</th>
                        <th className="text-right py-3 px-4 font-medium text-stone-500">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pageItems.map(p => {
                         const valor = (typeof p.volume_litros === 'number' && typeof p.preco_litro === 'number') ? (p.volume_litros * p.preco_litro) : null
                         const enviadoAoTanque = movimentos.some(m => m.origem_producao_id === p.id)
                         return (
                            <tr key={p.id} className="hover:bg-stone-50/50">
                               <td className="py-3 px-4">
                                  <div className="font-medium">{new Date(p.data).toLocaleDateString()}</div>
                                  <Badge variant="secondary" className={`${turnoBoxClasses(p.turno)} border-transparent mt-1`}>
                                    {TURNOS.find(t => t.value === p.turno)?.label || p.turno}
                                  </Badge>
                                  {enviadoAoTanque && (
                                    <div className="flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100 mt-1 w-fit" title="Enviado ao tanque">
                                      <Droplet className="w-3 h-3" /> Enviado
                                    </div>
                                  )}
                               </td>
                               <td className="py-3 px-4">
                                  <div className="font-medium text-lg">{typeof p.volume_litros === 'number' ? p.volume_litros.toFixed(2) : '-'} <span className="text-xs font-normal text-stone-500">L</span></div>
                               </td>
                               <td className="py-3 px-4">
                                  <div className="text-stone-600">R$ {typeof p.preco_litro === 'number' ? p.preco_litro.toFixed(2) : '-'} / L</div>
                                  <div className="font-medium text-stone-900">Total: R$ {valor !== null ? valor.toFixed(2) : '-'}</div>
                               </td>
                               <td className="py-3 px-4">
                                  <div className="capitalize">{CATEGORIAS.find(c => c.value === p.categoria)?.label || p.categoria}</div>
                                  {p.lote && <div className="text-xs text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded w-fit mt-1">{p.lote}</div>}
                               </td>
                               <td className="py-3 px-4">
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                     <div><span className="text-stone-500">G:</span> {p.gordura_pct ?? '-'}%</div>
                                     <div><span className="text-stone-500">P:</span> {p.proteina_pct ?? '-'}%</div>
                                     <div><span className="text-stone-500">CCS:</span> {p.ccs ?? '-'}</div>
                                     <div><span className="text-stone-500">CBT:</span> {p.cbt ?? '-'}</div>
                                  </div>
                               </td>
                               <td className="py-3 px-4 text-right">
                                  <div className="flex justify-end gap-2">
                                     <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingId(p.id); setFormData({ data: p.data || new Date().toISOString().split('T')[0], turno: p.turno || 'manha', volume_litros: p.volume_litros?.toString() || '', preco_litro: p.preco_litro?.toString() || '', categoria: p.categoria || 'bovino_leite', lote: p.lote || '', observacoes: p.observacoes || '', gordura_pct: p.gordura_pct || '', proteina_pct: p.proteina_pct || '', lactose_pct: p.lactose_pct || '', ccs: p.ccs || '', cbt: p.cbt || '', acidez_dornic: p.acidez_dornic || '', temperatura_c: p.temperatura_c || '' }); setDialogOpen(true) }} disabled={enviadoAoTanque}><Edit2 className="w-4 h-4" /></Button>
                                     <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-700 hover:text-blue-800 hover:bg-blue-50" onClick={() => setEnvioTanque({ open: true, producao: p, volume: p.volume_litros || 0, data: p.data || new Date().toISOString().split('T')[0], tanque_id: tanques[0]?.id || '', qualidade: { gordura_pct: p.gordura_pct, proteina_pct: p.proteina_pct, lactose_pct: p.lactose_pct, ccs: p.ccs, cbt: p.cbt, acidez_dornic: p.acidez_dornic, temperatura_c: p.temperatura_c } })} disabled={enviadoAoTanque}><Droplet className="w-4 h-4" /></Button>
                                     <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-red-600" onClick={() => deleteMutation.mutate(p.id)} disabled={enviadoAoTanque}><Trash2 className="w-4 h-4" /></Button>
                                  </div>
                               </td>
                            </tr>
                         )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
  </Card>

      {filtered.length > pageSize && (
        <Paginacao
          totalItems={filtered.length}
          currentPage={currentPage}
          onPageChange={(p) => setCurrentPage(p)}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1) }}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Relatórios de Qualidade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="p-3 bg-amber-50 rounded border border-amber-100 shadow-sm">
              <p className="text-amber-600 text-xs font-medium uppercase tracking-wide">Gordura média (%)</p>
              <p className="font-bold text-amber-800 text-lg">{qualidadeMedias.gordura?.toFixed(2) ?? '-'}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded border border-blue-100 shadow-sm">
              <p className="text-blue-600 text-xs font-medium uppercase tracking-wide">Proteína média (%)</p>
              <p className="font-bold text-blue-800 text-lg">{qualidadeMedias.proteina?.toFixed(2) ?? '-'}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded border border-orange-100 shadow-sm">
              <p className="text-orange-600 text-xs font-medium uppercase tracking-wide">Lactose média (%)</p>
              <p className="font-bold text-orange-800 text-lg">{qualidadeMedias.lactose?.toFixed(2) ?? '-'}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded border border-purple-100 shadow-sm">
              <p className="text-purple-600 text-xs font-medium uppercase tracking-wide">CCS média</p>
              <p className="font-bold text-purple-800 text-lg">{qualidadeMedias.ccs?.toFixed(0) ?? '-'}</p>
            </div>
            <div className="p-3 bg-teal-50 rounded border border-teal-100 shadow-sm">
              <p className="text-teal-600 text-xs font-medium uppercase tracking-wide">CBT média</p>
              <p className="font-bold text-teal-800 text-lg">{qualidadeMedias.cbt?.toFixed(0) ?? '-'}</p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-stone-600 text-sm mb-2">Tendência mensal de CCS/CBT</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                {tendenciaMensal.map(row => (
                  <div key={`ccs-${row.mes}`} className="grid grid-cols-6 items-center gap-2">
                    <div className="text-xs text-stone-600">{row.mes}</div>
                    <div className="col-span-4 h-2 bg-stone-100 rounded">
                      <div className="h-2 bg-[#2D6A4F] rounded" style={{ width: `${row.ccs ? Math.min(100, Math.round((row.ccs/1000)*100)) : 0}%` }} />
                    </div>
                    <div className="text-right text-xs text-stone-700">{row.ccs ?? '-'}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {tendenciaMensal.map(row => (
                  <div key={`cbt-${row.mes}`} className="grid grid-cols-6 items-center gap-2">
                    <div className="text-xs text-stone-600">{row.mes}</div>
                    <div className="col-span-4 h-2 bg-stone-100 rounded">
                      <div className="h-2 bg-[#D4A373] rounded" style={{ width: `${row.cbt ? Math.min(100, Math.round((row.cbt/1000)*100)) : 0}%` }} />
                    </div>
                    <div className="text-right text-xs text-stone-700">{row.cbt ?? '-'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tanques de Leite */}
      <Card className="mt-6">
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Tanques de Leite</CardTitle>
          <Dialog open={tanqueDialogOpen} onOpenChange={setTanqueDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2" onClick={() => setNovoTanque({ nome: '', capacidade_litros: '' })}>
                <Plus className="w-5 h-5" /> Novo Tanque
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Novo Tanque</DialogTitle>
                <DialogDescription>Cadastre um tanque para armazenar leite</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={novoTanque.nome} onChange={(e) => setNovoTanque({ ...novoTanque, nome: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Capacidade (L)</Label>
                  <Input type="number" step="0.01" value={novoTanque.capacidade_litros} onChange={(e) => setNovoTanque({ ...novoTanque, capacidade_litros: e.target.value })} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setTanqueDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={async () => {
                    const u = await agrogestao.auth.me()
                    await agrogestao.entities.TanqueLeite.create({ propriedade_id: propriedadeId, nome: novoTanque.nome, capacidade_litros: novoTanque.capacidade_litros ? parseFloat(novoTanque.capacidade_litros) : null, status: 'ativo', created_by: u?.email || null })
                    queryClient.invalidateQueries({ queryKey: ['tanques_leite', propriedadeId] })
                    setTanqueDialogOpen(false)
                  }} className="bg-[#40916C] hover:bg-[#2D6A4F]">Salvar</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {tanques.length === 0 ? (
            <div className="text-stone-600">Nenhum tanque cadastrado.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tanquesStats.map(t => {
                const isInativo = t.status === 'inativo'
                const capacity = t.capacidade_litros || 1
                const percentage = Math.min(100, (t.volume_atual / capacity) * 100)
                let colorClass = "bg-emerald-400"
                if (percentage > 50) colorClass = "bg-amber-400"
                if (percentage > 85) colorClass = "bg-red-400"
                
                return (
                  <motion.div 
                    key={t.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden flex flex-col ${isInativo ? 'opacity-75 grayscale' : ''}`}
                  >
                    <div className="p-4 border-b border-stone-100 flex justify-between items-start bg-stone-50/50">
                      <div>
                        <h3 className="font-bold text-stone-800 text-lg flex items-center gap-2">
                           {t.nome}
                           {isInativo && <Badge variant="secondary" className="text-[10px] h-5">Inativo</Badge>}
                        </h3>
                        <p className="text-xs text-stone-500">Capacidade: {t.capacidade_litros?.toLocaleString()} L</p>
                      </div>
                      <div className="text-right">
                         <div className="text-2xl font-bold text-stone-800">{t.volume_atual?.toLocaleString()} <span className="text-sm font-normal text-stone-500">L</span></div>
                         <div className={`text-xs font-medium ${percentage > 85 ? 'text-red-600' : 'text-emerald-600'}`}>
                           {percentage.toFixed(1)}% ocupado
                         </div>
                      </div>
                    </div>
                    
                    <div className="p-6 flex gap-6 items-center">
                       {/* Tank Visual */}
                       <div className="relative w-20 h-28 bg-stone-100 rounded-b-xl rounded-t-sm border-2 border-stone-300 overflow-hidden shadow-inner flex-shrink-0">
                          {/* Markers */}
                          <div className="absolute top-[25%] left-0 w-full h-[1px] bg-stone-300/50 z-10" />
                          <div className="absolute top-[50%] left-0 w-full h-[1px] bg-stone-300/50 z-10" />
                          <div className="absolute top-[75%] left-0 w-full h-[1px] bg-stone-300/50 z-10" />
                          
                          {/* Liquid */}
                          <motion.div 
                             className={`absolute bottom-0 left-0 w-full ${colorClass} transition-colors duration-500`}
                             initial={{ height: 0 }}
                             animate={{ height: `${percentage}%` }}
                             transition={{ type: "spring", stiffness: 50, damping: 15 }}
                          >
                             <div className="w-full h-full opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIiBmaWxsPSJub25lIiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMSI+PHBhdGggZD0iTTAgMTBsNS01IDUgNSA1LTUgNSA1IiBvcGFjaXR5PSIwLjIiLz48L3N2Zz4=')]"></div>
                          </motion.div>
                       </div>
                       
                       {/* Stats */}
                       <div className="flex-1 space-y-3">
                          <div className="space-y-1">
                             <Label className="text-xs text-stone-500 uppercase">Qualidade Média</Label>
                             {t.mediaGordura || t.mediaProteina ? (
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                   <div className="bg-amber-50 px-2 py-1 rounded text-amber-700 font-medium text-xs border border-amber-100">
                                      Gord. {t.mediaGordura?.toFixed(2)}%
                                   </div>
                                   <div className="bg-blue-50 px-2 py-1 rounded text-blue-700 font-medium text-xs border border-blue-100">
                                      Prot. {t.mediaProteina?.toFixed(2)}%
                                   </div>
                                </div>
                             ) : (
                                <p className="text-stone-400 text-xs italic">Sem dados de qualidade</p>
                             )}
                          </div>
                       </div>
                    </div>

                    <div className="p-3 bg-stone-50 border-t border-stone-100 grid grid-cols-2 gap-2 mt-auto">
                         <Button 
                            size="sm" 
                            variant={percentage > 0 ? "default" : "outline"}
                            className={percentage > 0 ? "bg-[#1B4332] hover:bg-[#2D6A4F] text-white" : "text-stone-400"}
                            onClick={() => setVendaTanque({ open: true, tanque: t, volume_max: t.volume_atual, data: new Date().toISOString().split('T')[0], volume_vendido: '', preco_litro: '', cliente: '', usar_qualidade: false, gordura_pct: t.mediaGordura?.toFixed(2) || '', proteina_pct: t.mediaProteina?.toFixed(2) || '', ref_gordura_pct: '3.5', ref_proteina_pct: '3.2', coef_gordura: '0.05', coef_proteina: '0.06' })}
                            disabled={isInativo || t.volume_atual <= 0}
                         >
                            Registrar Venda
                         </Button>
                         <Button 
                           size="sm" 
                           variant="ghost" 
                           className={isInativo ? "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" : "text-stone-500 hover:text-stone-700 hover:bg-stone-100"}
                           onClick={() => updateTanqueMutation.mutate({ id: t.id, status: isInativo ? 'ativo' : 'inativo' })}
                         >
                           {isInativo ? 'Ativar Tanque' : 'Inativar'}
                         </Button>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog envio para tanque */}
      <Dialog open={envioTanque.open} onOpenChange={(o) => {
          setEnvioTanque(prev => ({ ...prev, open: o }))
          if (!o) setErrorMsg("") 
        }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Droplet className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Enviar ao Tanque</DialogTitle>
                <DialogDescription>Transfira a produção para um tanque de resfriamento</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Coluna da Esquerda: Dados da Produção */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider">Origem (Produção)</h3>
              
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-600">Data da Produção</span>
                  <span className="font-medium">{envioTanque.producao?.data ? new Date(envioTanque.producao.data).toLocaleDateString() : '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-600">Volume Produzido</span>
                  <span className="font-medium text-stone-900">{envioTanque.producao?.volume_litros?.toFixed(2)} L</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-600">Turno</span>
                  <Badge variant="secondary" className="capitalize">{envioTanque.producao?.turno}</Badge>
                </div>
              </div>

              <div className="space-y-3">
                 <div className="space-y-1">
                    <Label className="text-xs text-stone-500">Data do Envio</Label>
                    <div className="relative">
                      <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-stone-400" />
                      <Input 
                        type="date" 
                        className="pl-9"
                        value={envioTanque.data} 
                        onChange={(e) => setEnvioTanque(prev => ({ ...prev, data: e.target.value }))} 
                      />
                    </div>
                 </div>
                 <div className="space-y-1">
                    <Label className="text-xs text-stone-500">Volume a Enviar (L)</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      value={envioTanque.volume} 
                      onChange={(e) => setEnvioTanque(prev => ({ ...prev, volume: e.target.value }))}
                      className="text-lg font-medium text-blue-700"
                    />
                 </div>
              </div>
            </div>

            {/* Coluna da Direita: Seleção do Tanque */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider">Destino (Tanque)</h3>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Selecione o Tanque</Label>
                  <Select value={envioTanque.tanque_id} onValueChange={(v) => {
                    setEnvioTanque(prev => ({ ...prev, tanque_id: v }))
                    setErrorMsg("")
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tanques.map(t => {
                        const stats = tanquesStats.find(ts => ts.id === t.id)
                        return (
                          <SelectItem key={t.id} value={t.id} disabled={stats?.disponivel <= 0}>
                            <div className="flex justify-between w-full gap-4">
                              <span>{t.nome}</span>
                              <span className="text-stone-400 text-xs">Disp: {stats?.disponivel?.toFixed(0)}L</span>
                            </div>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {envioTanque.tanque_id && (() => {
                  const tStats = tanquesStats.find(t => t.id === envioTanque.tanque_id)
                  const cap = tStats?.capacidade_litros || 1
                  const currentVol = tStats?.volume_atual || 0
                  const addVol = parseFloat(envioTanque.volume || 0)
                  const totalVol = currentVol + addVol
                  const pctCurrent = (currentVol / cap) * 100
                  const pctAdd = (addVol / cap) * 100
                  const isOver = totalVol > cap

                  return (
                    <div className="space-y-4 mt-4 p-4 border rounded-lg bg-stone-50/50">
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs font-medium">
                          <span className="text-stone-600">Ocupação do Tanque</span>
                          <span className={isOver ? "text-red-600" : "text-stone-600"}>
                            {totalVol.toFixed(1)} / {cap.toFixed(1)} L
                          </span>
                        </div>
                        <div className="h-4 bg-stone-200 rounded-full overflow-hidden flex">
                          <div className="bg-blue-500 h-full" style={{ width: `${Math.min(pctCurrent, 100)}%` }} />
                          <div className={`h-full ${isOver ? 'bg-red-500' : 'bg-blue-300'}`} style={{ width: `${Math.min(pctAdd, 100 - pctCurrent)}%` }} />
                        </div>
                        {isOver && (
                          <div className="flex items-center gap-2 text-xs text-red-600 mt-1">
                            <AlertCircle className="w-3 h-3" />
                            <span>Capacidade excedida em {(totalVol - cap).toFixed(1)} L</span>
                          </div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-stone-200 grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                           <Label className="text-xs">Gordura (%)</Label>
                           <Input 
                             type="number" 
                             className="h-8 text-xs"
                             value={envioTanque.qualidade.gordura_pct || ''} 
                             placeholder={envioTanque.producao?.gordura_pct || '-'}
                             onChange={(e) => setEnvioTanque(prev => ({ ...prev, qualidade: { ...prev.qualidade, gordura_pct: e.target.value } }))} 
                           />
                        </div>
                        <div className="space-y-1">
                           <Label className="text-xs">Proteína (%)</Label>
                           <Input 
                             type="number" 
                             className="h-8 text-xs"
                             value={envioTanque.qualidade.proteina_pct || ''} 
                             placeholder={envioTanque.producao?.proteina_pct || '-'}
                             onChange={(e) => setEnvioTanque(prev => ({ ...prev, qualidade: { ...prev.qualidade, proteina_pct: e.target.value } }))} 
                           />
                        </div>
                        <div className="space-y-1">
                           <Label className="text-xs">Acidez (°D)</Label>
                           <Input 
                             type="number" 
                             className="h-8 text-xs"
                             value={envioTanque.qualidade.acidez_dornic || ''} 
                             placeholder={envioTanque.producao?.acidez_dornic || '-'}
                             onChange={(e) => setEnvioTanque(prev => ({ ...prev, qualidade: { ...prev.qualidade, acidez_dornic: e.target.value } }))} 
                           />
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>

          {errorMsg && (
             <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">
               <AlertCircle className="w-4 h-4 shrink-0" />
               {errorMsg}
             </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t mt-2">
            <Button variant="ghost" onClick={() => {
              setEnvioTanque({ open: false, producao: null, tanque_id: '', volume: 0, data: new Date().toISOString().split('T')[0], qualidade: {} })
              setErrorMsg("")
            }}>Cancelar</Button>
            <Button 
              onClick={handleConfirmEnvioTanque} 
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              disabled={!envioTanque.tanque_id || parseFloat(envioTanque.volume || 0) <= 0}
            >
              Confirmar Envio
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog venda do tanque */}
      <Dialog open={vendaTanque.open} onOpenChange={(o) => setVendaTanque(prev => ({ ...prev, open: o }))}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700">
                <DollarSign className="w-5 h-5" />
              </div>
              Registrar Venda de Leite
            </DialogTitle>
            <DialogDescription>
              Lance a venda do tanque <strong>{vendaTanque.tanque?.nome}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Main Info Card */}
            <div className="bg-stone-50 p-4 rounded-xl border border-stone-100 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-stone-600">
                    <Calendar className="w-4 h-4" /> Data
                  </Label>
                  <Input type="date" value={vendaTanque.data} onChange={(e) => setVendaTanque(prev => ({ ...prev, data: e.target.value }))} className="bg-white" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-stone-600">
                    <Droplet className="w-4 h-4" /> Volume (L)
                  </Label>
                  <div className="relative">
                    <Input type="number" step="0.01" value={vendaTanque.volume_vendido} onChange={(e) => setVendaTanque(prev => ({ ...prev, volume_vendido: e.target.value }))} className="bg-white pr-16" />
                    <span className="absolute right-3 top-2.5 text-xs text-stone-400 font-medium">máx {vendaTanque.volume_max?.toFixed(0)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-stone-600">
                    <DollarSign className="w-4 h-4" /> Preço Base (R$)
                  </Label>
                  <Input type="number" step="0.01" value={vendaTanque.preco_litro} onChange={(e) => setVendaTanque(prev => ({ ...prev, preco_litro: e.target.value }))} className="bg-white" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-stone-600">
                  <Users className="w-4 h-4" /> Cliente / Comprador
                </Label>
                <Popover modal={true} open={openCombobox} onOpenChange={setOpenCombobox}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCombobox}
                      className="w-full justify-between bg-white font-normal"
                    >
                      {vendaTanque.cliente
                        ? parceiros.find((p) => p.nome === vendaTanque.cliente)?.nome || vendaTanque.cliente
                        : "Selecione um parceiro..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar parceiro..." />
                      <CommandList>
                        <CommandEmpty>Nenhum parceiro encontrado.</CommandEmpty>
                        <CommandGroup>
                          {parceiros.map((p) => {
                            const pNome = p.nome || p.nome_razao || p.razao_social
                            const pDoc = p.documento || p.cnpj_cpf
                            return (
                              <CommandItem
                                key={p.id}
                                value={pNome || ''}
                                keywords={[pNome, pDoc, p.email].filter(Boolean)}
                                onSelect={() => {
                                  const cliente = pNome
                                  const tanqueId = vendaTanque.tanque?.id
                                  const cfg = getCoef(cliente, tanqueId)
                                  setVendaTanque(prev => ({
                                    ...prev,
                                    cliente: cliente === prev.cliente ? "" : cliente,
                                    ...(cfg && cliente !== prev.cliente ? {
                                      ref_gordura_pct: String(cfg.ref_gordura_pct ?? prev.ref_gordura_pct),
                                      ref_proteina_pct: String(cfg.ref_proteina_pct ?? prev.ref_proteina_pct),
                                      coef_gordura: String(cfg.coef_gordura ?? prev.coef_gordura),
                                      coef_proteina: String(cfg.coef_proteina ?? prev.coef_proteina)
                                    } : {})
                                  }))
                                  setOpenCombobox(false)
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    vendaTanque.cliente === pNome ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span>{pNome}</span>
                                  {pDoc && <span className="text-xs text-stone-500">{pDoc}</span>}
                                </div>
                              </CommandItem>
                            )
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {parceiros.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Nenhum parceiro cadastrado. Cadastre em Parceiros.</p>
                )}
              </div>
            </div>

            {/* Quality Adjustments */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded text-blue-700">
                    <Calculator className="w-4 h-4" />
                  </div>
                  <Label className="text-base font-semibold text-stone-800 cursor-pointer" htmlFor="check-qualidade">Ajuste por Qualidade</Label>
                </div>
                <Checkbox id="check-qualidade" checked={!!vendaTanque.usar_qualidade} onCheckedChange={(checked) => setVendaTanque(prev => ({ ...prev, usar_qualidade: !!checked }))} />
              </div>

              <AnimatePresence>
                {vendaTanque.usar_qualidade && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-wide text-stone-500">Parâmetros Atuais</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-stone-600 mb-1 block">Gordura (%)</Label>
                              <Input type="number" step="0.01" value={vendaTanque.gordura_pct} onChange={(e) => setVendaTanque(prev => ({ ...prev, gordura_pct: e.target.value }))} className="bg-white h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-xs text-stone-600 mb-1 block">Proteína (%)</Label>
                              <Input type="number" step="0.01" value={vendaTanque.proteina_pct} onChange={(e) => setVendaTanque(prev => ({ ...prev, proteina_pct: e.target.value }))} className="bg-white h-8 text-sm" />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-wide text-stone-500">Parâmetros Base</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-stone-600 mb-1 block">Ref. Gordura</Label>
                              <Input type="number" step="0.01" value={vendaTanque.ref_gordura_pct} onChange={(e) => setVendaTanque(prev => ({ ...prev, ref_gordura_pct: e.target.value }))} className="bg-white h-8 text-sm" />
                            </div>
                            <div>
                              <Label className="text-xs text-stone-600 mb-1 block">Ref. Proteína</Label>
                              <Input type="number" step="0.01" value={vendaTanque.ref_proteina_pct} onChange={(e) => setVendaTanque(prev => ({ ...prev, ref_proteina_pct: e.target.value }))} className="bg-white h-8 text-sm" />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-blue-200/50">
                        <div className="space-y-1">
                          <Label className="text-xs text-stone-600">Coef. Gordura (R$/%)</Label>
                          <Input type="number" step="0.01" value={vendaTanque.coef_gordura} onChange={(e) => setVendaTanque(prev => ({ ...prev, coef_gordura: e.target.value }))} className="bg-white h-8 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-stone-600">Coef. Proteína (R$/%)</Label>
                          <Input type="number" step="0.01" value={vendaTanque.coef_proteina} onChange={(e) => setVendaTanque(prev => ({ ...prev, coef_proteina: e.target.value }))} className="bg-white h-8 text-sm" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Total Section */}
            <div className="bg-stone-900 text-stone-100 p-4 rounded-xl flex items-center justify-between shadow-lg">
              <div>
                <p className="text-xs text-stone-400 uppercase font-semibold tracking-wider">Valor Final</p>
                <p className="text-sm text-stone-300 mt-1">
                  Preço/L: R$ {(() => {
                    const preco = parseFloat(vendaTanque.preco_litro || 0)
                    if (!vendaTanque.usar_qualidade) return preco.toFixed(2)
                    const g = parseFloat(vendaTanque.gordura_pct || 0)
                    const p = parseFloat(vendaTanque.proteina_pct || 0)
                    const rg = parseFloat(vendaTanque.ref_gordura_pct || 0)
                    const rp = parseFloat(vendaTanque.ref_proteina_pct || 0)
                    const cg = parseFloat(vendaTanque.coef_gordura || 0)
                    const cp = parseFloat(vendaTanque.coef_proteina || 0)
                    const aj = (g - rg) * cg + (p - rp) * cp
                    return (preco + aj).toFixed(2)
                  })()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-emerald-400">
                  R$ {(() => {
                    const vol = parseFloat(vendaTanque.volume_vendido || 0)
                    const preco = parseFloat(vendaTanque.preco_litro || 0)
                    let finalPreco = preco
                    if (vendaTanque.usar_qualidade) {
                      const g = parseFloat(vendaTanque.gordura_pct || 0)
                      const p = parseFloat(vendaTanque.proteina_pct || 0)
                      const rg = parseFloat(vendaTanque.ref_gordura_pct || 0)
                      const rp = parseFloat(vendaTanque.ref_proteina_pct || 0)
                      const cg = parseFloat(vendaTanque.coef_gordura || 0)
                      const cp = parseFloat(vendaTanque.coef_proteina || 0)
                      const aj = (g - rg) * cg + (p - rp) * cp
                      finalPreco = preco + aj
                    }
                    return (vol * finalPreco).toFixed(2)
                  })()}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" onClick={() => setVendaTanque({ open: false, tanque: null, volume_max: 0, data: new Date().toISOString().split('T')[0], volume_vendido: '', preco_litro: '', cliente: '', usar_qualidade: false, gordura_pct: '', proteina_pct: '', ref_gordura_pct: '3.5', ref_proteina_pct: '3.2', coef_gordura: '0.05', coef_proteina: '0.06' })} className="h-11 px-6">
              Cancelar
            </Button>
            <Button disabled={isSavingVenda} onClick={async () => {
              const vol = parseFloat(vendaTanque.volume_vendido || 0)
              const preco = parseFloat(vendaTanque.preco_litro || 0)
              if (!vol || vol <= 0) { setErrorMsg('Informe o volume'); return }
              if (vol > (vendaTanque.volume_max || 0)) { setErrorMsg('Volume maior que disponível'); return }
              
              setIsSavingVenda(true)
              try {
                const u = await agrogestao.auth.me()
                let conta = null
                let precoFinal = preco
                let valorFinal = preco * vol
                if (vendaTanque.usar_qualidade) {
                  const g = parseFloat(vendaTanque.gordura_pct || 0)
                  const p = parseFloat(vendaTanque.proteina_pct || 0)
                  const rg = parseFloat(vendaTanque.ref_gordura_pct || 0)
                  const rp = parseFloat(vendaTanque.ref_proteina_pct || 0)
                  const cg = parseFloat(vendaTanque.coef_gordura || 0)
                  const cp = parseFloat(vendaTanque.coef_proteina || 0)
                  const ajuste = (g - rg) * cg + (p - rp) * cp
                  precoFinal = preco + ajuste
                  valorFinal = vol * precoFinal
                  if (vendaTanque.cliente && vendaTanque.tanque?.id) {
                    setCoef(vendaTanque.cliente, vendaTanque.tanque.id, {
                      ref_gordura_pct: rg,
                      ref_proteina_pct: rp,
                      coef_gordura: cg,
                      coef_proteina: cp
                    })
                  }
                }
                if (precoFinal && vol) {
                  conta = await agrogestao.entities.ContaReceber.create({
                    propriedade_id: propriedadeId,
                    descricao: vendaTanque.usar_qualidade ? `Venda de Leite - Tanque ${vendaTanque.tanque.nome} (${vol.toFixed(2)} L) • preço ajustado por qualidade` : `Venda de Leite - Tanque ${vendaTanque.tanque.nome} (${vol.toFixed(2)} L)`,
                    valor: valorFinal,
                    data_vencimento: vendaTanque.data,
                    categoria: 'Venda de Leite',
                    cliente: vendaTanque.cliente || '',
                    status: 'pendente'
                  })
                }
                await agrogestao.entities.MovimentacaoTanqueLeite.create({
                  propriedade_id: propriedadeId,
                  tanque_id: vendaTanque.tanque.id,
                  tipo: 'saida',
                  data: vendaTanque.data,
                  volume_litros: vol,
                  cliente: vendaTanque.cliente || null,
                  preco_litro: precoFinal || null,
                  valor_total: precoFinal && vol ? valorFinal : null,
                  descricao: vendaTanque.usar_qualidade ? `Venda de leite do tanque ${vendaTanque.tanque.nome} • preço ajustado por qualidade` : `Venda de leite do tanque ${vendaTanque.tanque.nome}`,
                  conta_receber_id: conta?.id || null,
                  created_by: u?.email || null
                })
                queryClient.invalidateQueries({ queryKey: ['movimentacoes_tanque_leite', propriedadeId] })
                queryClient.invalidateQueries({ queryKey: ['contas_receber', propriedadeId] })
                setVendaTanque({ open: false, tanque: null, volume_max: 0, data: new Date().toISOString().split('T')[0], volume_vendido: '', preco_litro: '', cliente: '', usar_qualidade: false, gordura_pct: '', proteina_pct: '', ref_gordura_pct: '3.5', ref_proteina_pct: '3.2', coef_gordura: '0.05', coef_proteina: '0.06' })
              } catch (err) {
                setErrorMsg('Erro ao salvar venda')
                console.error(err)
              } finally {
                setIsSavingVenda(false)
              }
            }} className="bg-[#40916C] hover:bg-[#2D6A4F] h-11 px-8 text-base font-medium shadow-md hover:shadow-lg transition-all">
              {isSavingVenda ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5 mr-2" />
                  Confirmar Venda
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="mt-6">
        <CardHeader className="border-b pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
             <CardTitle>Movimentações de Tanque</CardTitle>
             <div className="flex flex-wrap gap-2 items-center">
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Tipo</Label>
                 <Select value={movFilter.tipo} onValueChange={(v) => setMovFilter({ ...movFilter, tipo: v })}>
                   <SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                   <SelectContent>
                     <SelectItem value="todos">Todos</SelectItem>
                     <SelectItem value="entrada">Entrada</SelectItem>
                     <SelectItem value="saida">Saída</SelectItem>
                   </SelectContent>
                 </Select>
               </div>
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Início</Label>
                 <Input type="date" className="h-8 text-xs w-32" value={movFilter.inicio} onChange={(e) => setMovFilter({ ...movFilter, inicio: e.target.value })} />
               </div>
               <div className="space-y-0.5">
                 <Label className="text-[10px] uppercase text-stone-500">Fim</Label>
                 <Input type="date" className="h-8 text-xs w-32" value={movFilter.fim} onChange={(e) => setMovFilter({ ...movFilter, fim: e.target.value })} />
               </div>
             </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {filteredMovimentos.length === 0 ? (
            <div className="text-stone-600 text-sm py-4 text-center bg-stone-50 rounded-lg">Nenhuma movimentação encontrada com os filtros selecionados.</div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-stone-500">Data</th>
                      <th className="text-left py-2 px-3 font-medium text-stone-500">Tanque</th>
                      <th className="text-left py-2 px-3 font-medium text-stone-500">Tipo</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-500">Volume (L)</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-500">Conta</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-500">Lançamento</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {movPageItems.map(m => {
                      const tanque = tanques.find(t => t.id === m.tanque_id)
                      const isEntrada = m.tipo === 'entrada'
                      return (
                        <tr key={m.id} className="hover:bg-stone-50/50">
                          <td className="py-2 px-3">{new Date(m.data).toLocaleDateString()}</td>
                          <td className="py-2 px-3 font-medium">{tanque?.nome || '-'}</td>
                          <td className="py-2 px-3">
                            <Badge variant={isEntrada ? "outline" : "default"} className={isEntrada ? "text-blue-700 border-blue-200 bg-blue-50" : "bg-emerald-600 hover:bg-emerald-700"}>
                              {isEntrada ? 'Entrada' : 'Saída'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-stone-700">{(m.volume_litros || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right">
                            {m.conta_receber_id ? (
                              <a className="text-blue-600 hover:text-blue-800 hover:underline text-xs" href={`${createPageUrl('ContasReceber')}?conta_id=${m.conta_receber_id}`}>Ver conta</a>
                            ) : (
                              <span className="text-stone-300">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {m.lancamento_id ? (
                              <a className="text-blue-600 hover:text-blue-800 hover:underline text-xs" href={`${createPageUrl('Financeiro')}?lancamento_id=${m.lancamento_id}`}>Ver lançamento</a>
                            ) : (
                              <span className="text-stone-300">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <Paginacao 
                currentPage={movPage}
                totalPages={Math.ceil(filteredMovimentos.length / movPageSize)}
                onPageChange={setMovPage}
                totalItems={filteredMovimentos.length}
                pageSize={movPageSize}
                onPageSizeChange={setMovPageSize}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
